import { useObserver, useSubtree } from "subtree.js/react";
import { ShareButton } from "../../components/ShareButton";
import { CustomerQrModal } from "../../sections/customer-qr/customer-qr-modal";
import { LocationsSection } from "./sections/locations/locations-section";
import { ProfileSection } from "./sections/profile/profile-section";
import { ProvidersSection } from "./sections/providers/providers-section";
import { SalesTaxSection } from "./sections/sales-tax/sales-tax-section";
import { ShopAdminsSection } from "./sections/shop-admins/shop-admins-section";
import { WebsiteSection } from "./sections/website/website-section";
import { StoreSettingsActions, StoreSettingsState } from "./store-settings-model";

function StoreSkeleton() {
  return (
    <div className="mx-auto max-w-2xl space-y-6 px-4 py-8 sm:px-6">
      <div className="h-8 w-56 animate-pulse rounded bg-surface-strong" />
      <div className="h-96 animate-pulse rounded-card bg-surface-strong" />
    </div>
  );
}

export function StoreSettingsPage() {
  const state = useSubtree(StoreSettingsState);
  const actions = useSubtree(StoreSettingsActions);
  const observer = useObserver();
  const status = observer.watch(state.status);
  const store = observer.watch(state.store);
  const tab = observer.watch(state.tab);
  const tabs = observer.watch(state.tabs);
  const callbackMessage = observer.watch(state.callbackMessage);
  const share = observer.watch(state.share);
  const generalAllowed = tabs.some((t) => t.id === "general");

  if (status.kind === "idle" || status.kind === "loading") return <StoreSkeleton />;
  if (status.kind === "error" || !store) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-muted">
        We couldn’t find this Store.{" "}
        <button type="button" onClick={actions.toDashboard} className="ml-1 underline">
          Go to dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-8 px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-muted">Store settings</p>
          <h1 className="mt-1 text-2xl font-bold text-white">{store.name ?? "Store"}</h1>
          <p className="mt-2 text-sm text-muted">
            Manage the business profile independently from its commerce providers.
          </p>
        </div>
        {/* Also on Customer Scans, where a shop with no customers yet needs it.
            It belongs here too: this is where a merchant looks when they want to
            reprint the sign on the counter. */}
        <div className="flex shrink-0 flex-wrap gap-2">
          {share && (
            <ShareButton
              target={share}
              heading="Share your shop page"
              label="Share shop page"
              note="Your page on hobbyshop.ai: your breaks, card shows, hours and how to find you. It opens in the HobbyScan app for anyone who has it."
            />
          )}
          {generalAllowed && (
            <button type="button" onClick={actions.showQr} className="btn-secondary btn-sm">
              Show QR code
            </button>
          )}
        </div>
      </div>

      {generalAllowed && <CustomerQrModal />}

      {callbackMessage && (
        <p className={`note ${callbackMessage.error ? "note-danger text-danger" : "note-accent text-accent"}`}>
          {callbackMessage.text}
        </p>
      )}

      <nav className="flex gap-1 border-b border-line">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => actions.selectTab(item.id)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              tab === item.id ? "border-accent text-white" : "border-transparent text-muted hover:text-white"
            }`}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {tab === "general" ? (
        <>
          <section className="panel p-6 sm:p-8">
            <ProfileSection />
          </section>

          <LocationsSection />

          {/* Above Providers on purpose: Stripe reads this address when it creates
              the account, so the order matches the order things have to happen in. */}
          <WebsiteSection />

          <ProvidersSection />

          {/* Below Providers: it is meaningless without a connected Stripe account,
              and the registrations that decide what it charges live in Stripe. */}
          <SalesTaxSection />
        </>
      ) : (
        <ShopAdminsSection />
      )}
    </div>
  );
}
